import React, { useState, useEffect } from 'react';
import { getIncidentCategories, createIncident, getOrganizations } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import {
  AlertTriangle,
  User,
  Users,
  MapPin,
  Calendar,
  Clock,
  FileText,
  Shield,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Building2,
  Heart,
  Wrench,
  Clipboard,
  Cloud,
  Send,
  Eye,
  EyeOff,
  Phone,
  Mail,
  Home,
  UserCheck,
  Scale
} from 'lucide-react';

// Category icons mapping
const CATEGORY_ICONS = {
  abuse_misconduct: AlertTriangle,
  injury_medical: Heart,
  facility_premises: Building2,
  equipment: Wrench,
  operational: Clipboard,
  behavior_conduct: Users,
  environmental: Cloud,
  other_safety: Shield,
};

export default function IncidentReportForm({ 
  formType = 'sports', // 'sports' or 'fitness'
  organizationId,
  onSuccess,
  onCancel 
}) {
  const { user } = useAuth();
  const { selectedOrg } = useAdminMode();
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showLegalSection, setShowLegalSection] = useState(false);
  
  // Determine org ID
  const currentOrgId = organizationId || selectedOrg?.id || user?.organization_id;
  
  // Form state
  const [formData, setFormData] = useState({
    // Reporter
    reporter_anonymous: false,
    reporter_name: '',
    reporter_contact_method: '',
    reporter_contact_details: '',
    
    // Classification
    primary_category: '',
    secondary_type: '',
    
    // Incident Details
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    location: '',
    description: '',
    is_near_miss: false,
    reported_to_authorities: false,
    authority_details: '',
    
    // Subject
    subject_name: '',
    subject_age: '',
    subject_age_approximate: '',
    subject_gender: '',
    subject_affiliation: '',
    subject_whereabouts: '',
    multiple_subjects: false,
    
    // Guardian
    guardian_name: '',
    guardian_relationship: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
    
    // Accused
    accused_name: '',
    accused_role: '',
    accused_organization: '',
    
    // Witnesses
    witnesses: [''],
    
    // Severity
    severity: 'medium',
    
    // Legal
    legal_acknowledgment: false,
    mandatory_reporter_acknowledged: false,
  });

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getIncidentCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load incident categories');
    } finally {
      setLoading(false);
    }
  };

  // Check if we need legal section
  useEffect(() => {
    const needsLegal = formData.primary_category === 'abuse_misconduct';
    setShowLegalSection(needsLegal);
  }, [formData.primary_category]);

  // Check if subject is a minor
  const isMinor = formData.subject_age_approximate === 'minor_13_17' || 
                  formData.subject_age_approximate === 'minor_under_13' ||
                  (formData.subject_age && parseInt(formData.subject_age) < 18);

  // Labels based on form type
  const subjectLabel = formType === 'sports' ? 'Athlete / Participant' : 'Member / Participant';
  const affiliationLabel = formType === 'sports' ? 'Team / Organization' : 'Membership Type / Class';

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addWitness = () => {
    setFormData(prev => ({ ...prev, witnesses: [...prev.witnesses, ''] }));
  };

  const updateWitness = (index, value) => {
    const newWitnesses = [...formData.witnesses];
    newWitnesses[index] = value;
    setFormData(prev => ({ ...prev, witnesses: newWitnesses }));
  };

  const removeWitness = (index) => {
    const newWitnesses = formData.witnesses.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, witnesses: newWitnesses.length ? newWitnesses : [''] }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.primary_category) {
      toast.error('Please select an incident category');
      setCurrentStep(1);
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please provide a description of the incident');
      setCurrentStep(2);
      return;
    }
    if (showLegalSection && !formData.legal_acknowledgment) {
      toast.error('Please acknowledge the legal disclaimer');
      setCurrentStep(5);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        organization_id: currentOrgId,
        form_type: formType,
        ...formData,
        witnesses: formData.witnesses.filter(w => w.trim()),
        subject_age: formData.subject_age ? parseInt(formData.subject_age) : null,
      };

      await createIncident(payload);
      toast.success('Incident report submitted successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to submit incident:', error);
      toast.error('Failed to submit incident report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalSteps = showLegalSection ? 5 : 4;

  return (
    <div className="max-w-3xl mx-auto" data-testid="incident-report-form">
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
            <div key={step} className="flex items-center">
              <button
                onClick={() => setCurrentStep(step)}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep === step
                    ? 'bg-primary text-primary-foreground'
                    : currentStep > step
                    ? 'bg-green-500 text-white'
                    : 'bg-secondary text-muted-foreground'
                }`}
              >
                {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
              </button>
              {step < totalSteps && (
                <div className={`w-full h-1 mx-2 ${
                  currentStep > step ? 'bg-green-500' : 'bg-secondary'
                }`} style={{ minWidth: '40px' }} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Category</span>
          <span>Details</span>
          <span>{subjectLabel}</span>
          <span>Review</span>
          {showLegalSection && <span>Legal</span>}
        </div>
      </div>

      {/* Step 1: Category Selection */}
      {currentStep === 1 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
              Incident Category
            </CardTitle>
            <CardDescription>
              Select the primary category that best describes this incident
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Category */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories?.primary_categories?.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.value] || Shield;
                const isSelected = formData.primary_category === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => {
                      updateField('primary_category', cat.value);
                      updateField('secondary_type', ''); // Reset secondary
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-center ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50 hover:bg-secondary/50'
                    }`}
                    data-testid={`category-${cat.value}`}
                  >
                    <Icon className={`w-8 h-8 mx-auto mb-2 ${
                      isSelected ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isSelected ? 'text-primary' : ''
                    }`}>{cat.label}</span>
                    {cat.requires_legal && (
                      <Badge className="mt-2 bg-[#FF9500]/20 text-[#FF9500] text-xs">
                        Legal Required
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Secondary Type (Dynamic) */}
            {formData.primary_category && categories?.secondary_types?.[formData.primary_category] && (
              <div className="space-y-2">
                <Label>Incident Type</Label>
                <Select
                  value={formData.secondary_type}
                  onValueChange={(v) => updateField('secondary_type', v)}
                >
                  <SelectTrigger data-testid="secondary-type-select">
                    <SelectValue placeholder="Select specific incident type" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.secondary_types[formData.primary_category].map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Severity */}
            <div className="space-y-2">
              <Label>Severity Level</Label>
              <div className="flex gap-2">
                {categories?.severity_levels?.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => updateField('severity', level.value)}
                    className={`flex-1 py-2 px-4 rounded-lg border transition-all ${
                      formData.severity === level.value
                        ? level.value === 'critical' ? 'border-red-500 bg-red-500/20 text-red-500' :
                          level.value === 'high' ? 'border-orange-500 bg-orange-500/20 text-orange-500' :
                          level.value === 'medium' ? 'border-yellow-500 bg-yellow-500/20 text-yellow-500' :
                          'border-green-500 bg-green-500/20 text-green-500'
                        : 'border-border hover:bg-secondary/50'
                    }`}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => setCurrentStep(2)}
              disabled={!formData.primary_category}
            >
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 2: Incident Details */}
      {currentStep === 2 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
              Incident Details
            </CardTitle>
            <CardDescription>
              Provide details about when and where the incident occurred
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Reporter Info */}
            <div className="space-y-4 p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Reporter Information</h3>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="anonymous"
                  checked={formData.reporter_anonymous}
                  onCheckedChange={(checked) => updateField('reporter_anonymous', checked)}
                />
                <Label htmlFor="anonymous" className="cursor-pointer">
                  Report Anonymously
                </Label>
              </div>

              {!formData.reporter_anonymous && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Your Name</Label>
                    <Input
                      value={formData.reporter_name}
                      onChange={(e) => updateField('reporter_name', e.target.value)}
                      placeholder="Full name"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Contact Method</Label>
                    <Select
                      value={formData.reporter_contact_method}
                      onValueChange={(v) => updateField('reporter_contact_method', v)}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue placeholder="Select contact method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.reporter_contact_method && (
                    <div className="space-y-2 md:col-span-2">
                      <Label>{formData.reporter_contact_method === 'email' ? 'Email Address' : 'Phone Number'}</Label>
                      <Input
                        value={formData.reporter_contact_details}
                        onChange={(e) => updateField('reporter_contact_details', e.target.value)}
                        placeholder={formData.reporter_contact_method === 'email' ? 'email@example.com' : '(555) 555-5555'}
                        className="bg-secondary/50"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Date/Time/Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date of Incident *
                </Label>
                <Input
                  type="date"
                  value={formData.incident_date}
                  onChange={(e) => updateField('incident_date', e.target.value)}
                  className="bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Time (Optional)
                </Label>
                <Input
                  type="time"
                  value={formData.incident_time}
                  onChange={(e) => updateField('incident_time', e.target.value)}
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="Where did this occur?"
                  className="bg-secondary/50"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Description *
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Provide a detailed description of what happened..."
                className="bg-secondary/50 min-h-[150px]"
                required
              />
            </div>

            {/* Flags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 p-3 rounded-lg border border-border">
                <Checkbox
                  id="near-miss"
                  checked={formData.is_near_miss}
                  onCheckedChange={(checked) => updateField('is_near_miss', checked)}
                />
                <Label htmlFor="near-miss" className="cursor-pointer">
                  This was a <strong>Near Miss</strong> (no injury/harm occurred)
                </Label>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-border">
                <Checkbox
                  id="authorities"
                  checked={formData.reported_to_authorities}
                  onCheckedChange={(checked) => updateField('reported_to_authorities', checked)}
                />
                <Label htmlFor="authorities" className="cursor-pointer">
                  Reported to Authorities
                </Label>
              </div>
            </div>

            {formData.reported_to_authorities && (
              <div className="space-y-2">
                <Label>Authority Details</Label>
                <Input
                  value={formData.authority_details}
                  onChange={(e) => updateField('authority_details', e.target.value)}
                  placeholder="Which authority was contacted and when?"
                  className="bg-secondary/50"
                />
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={() => setCurrentStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              onClick={() => setCurrentStep(3)}
              disabled={!formData.description.trim()}
            >
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 3: Subject Information */}
      {currentStep === 3 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
              {subjectLabel} Information
            </CardTitle>
            <CardDescription>
              Provide information about the individual(s) involved in this incident
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Subject Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{subjectLabel} Name</Label>
                <Input
                  value={formData.subject_name}
                  onChange={(e) => updateField('subject_name', e.target.value)}
                  placeholder="Name or 'Anonymous' / 'Unknown'"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Age or Age Range</Label>
                <Select
                  value={formData.subject_age_approximate}
                  onValueChange={(v) => updateField('subject_age_approximate', v)}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select age range" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.age_ranges?.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={formData.subject_gender}
                  onValueChange={(v) => updateField('subject_gender', v)}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-Binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{affiliationLabel}</Label>
                <Input
                  value={formData.subject_affiliation}
                  onChange={(e) => updateField('subject_affiliation', e.target.value)}
                  placeholder={formType === 'sports' ? "Team or program name" : "Membership type or class"}
                  className="bg-secondary/50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Whereabouts at Time of Incident</Label>
              <Input
                value={formData.subject_whereabouts}
                onChange={(e) => updateField('subject_whereabouts', e.target.value)}
                placeholder="Where was this person at the time?"
                className="bg-secondary/50"
              />
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg border border-border">
              <Checkbox
                id="multiple"
                checked={formData.multiple_subjects}
                onCheckedChange={(checked) => updateField('multiple_subjects', checked)}
              />
              <Label htmlFor="multiple" className="cursor-pointer">
                Multiple {formType === 'sports' ? 'athletes/participants' : 'members'} were involved
              </Label>
            </div>

            {/* Guardian Section - Show for minors or abuse cases */}
            {(isMinor || formData.primary_category === 'abuse_misconduct') && (
              <div className="space-y-4 p-4 rounded-lg border-2 border-[#FF9500]/30 bg-[#FF9500]/5">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FF9500]" />
                  <h3 className="font-semibold text-[#FF9500]">Parent/Guardian Information</h3>
                  {isMinor && <Badge className="bg-[#FF9500]/20 text-[#FF9500]">Required for Minors</Badge>}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parent/Guardian Name</Label>
                    <Input
                      value={formData.guardian_name}
                      onChange={(e) => updateField('guardian_name', e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship</Label>
                    <Input
                      value={formData.guardian_relationship}
                      onChange={(e) => updateField('guardian_relationship', e.target.value)}
                      placeholder="e.g., Parent, Legal Guardian"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone
                    </Label>
                    <Input
                      value={formData.guardian_phone}
                      onChange={(e) => updateField('guardian_phone', e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Label>
                    <Input
                      value={formData.guardian_email}
                      onChange={(e) => updateField('guardian_email', e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="flex items-center gap-2">
                      <Home className="w-4 h-4" /> Address
                    </Label>
                    <Input
                      value={formData.guardian_address}
                      onChange={(e) => updateField('guardian_address', e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Accused/Involved Party */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-semibold">Accused / Involved Party (if applicable)</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.accused_name}
                    onChange={(e) => updateField('accused_name', e.target.value)}
                    placeholder="Name or 'Unknown'"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={formData.accused_role}
                    onValueChange={(v) => updateField('accused_role', v)}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.accused_roles?.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Organization</Label>
                  <Input
                    value={formData.accused_organization}
                    onChange={(e) => updateField('accused_organization', e.target.value)}
                    placeholder="Affiliated organization"
                    className="bg-secondary/50"
                  />
                </div>
              </div>
            </div>

            {/* Witnesses */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-semibold">Witnesses</h3>
                </div>
                <Button variant="outline" size="sm" onClick={addWitness}>
                  Add Witness
                </Button>
              </div>
              
              {formData.witnesses.map((witness, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={witness}
                    onChange={(e) => updateWitness(index, e.target.value)}
                    placeholder={`Witness ${index + 1} name`}
                    className="bg-secondary/50"
                  />
                  {formData.witnesses.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeWitness(index)}>
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={() => setCurrentStep(2)}>
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button onClick={() => setCurrentStep(4)}>
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 4: Review */}
      {currentStep === 4 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
              Review Your Report
            </CardTitle>
            <CardDescription>
              Please review the information before submitting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/30">
                <h3 className="font-semibold mb-3">Incident Classification</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <p className="font-medium">
                      {categories?.primary_categories?.find(c => c.value === formData.primary_category)?.label}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <p className="font-medium">{formData.secondary_type || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Severity:</span>
                    <Badge className={
                      formData.severity === 'critical' ? 'bg-red-500/20 text-red-500' :
                      formData.severity === 'high' ? 'bg-orange-500/20 text-orange-500' :
                      formData.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-green-500/20 text-green-500'
                    }>
                      {formData.severity.charAt(0).toUpperCase() + formData.severity.slice(1)}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date:</span>
                    <p className="font-medium">{formData.incident_date}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-secondary/30">
                <h3 className="font-semibold mb-3">Description</h3>
                <p className="text-sm">{formData.description}</p>
              </div>

              {formData.subject_name && (
                <div className="p-4 rounded-lg bg-secondary/30">
                  <h3 className="font-semibold mb-3">{subjectLabel}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <p className="font-medium">{formData.subject_name}</p>
                    </div>
                    {formData.subject_age_approximate && (
                      <div>
                        <span className="text-muted-foreground">Age:</span>
                        <p className="font-medium">
                          {categories?.age_ranges?.find(r => r.value === formData.subject_age_approximate)?.label}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {formData.is_near_miss && (
                <Alert className="border-blue-500/30 bg-blue-500/5">
                  <AlertCircle className="h-4 w-4 text-blue-500" />
                  <AlertDescription>
                    This incident has been marked as a <strong>Near Miss</strong>
                  </AlertDescription>
                </Alert>
              )}

              {formData.reported_to_authorities && (
                <Alert className="border-green-500/30 bg-green-500/5">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription>
                    This incident has been reported to authorities
                    {formData.authority_details && `: ${formData.authority_details}`}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={() => setCurrentStep(3)}>
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {showLegalSection ? (
              <Button onClick={() => setCurrentStep(5)}>
                Next <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Report
              </Button>
            )}
          </CardFooter>
        </Card>
      )}

      {/* Step 5: Legal Acknowledgments (Only for Abuse/Misconduct) */}
      {currentStep === 5 && showLegalSection && (
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Scale className="w-6 h-6 text-[#FF9500]" />
              <div>
                <CardTitle className="text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
                  Legal Acknowledgments
                </CardTitle>
                <CardDescription>
                  This incident type requires legal acknowledgments before submission
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Legal Disclaimers */}
            <Alert className="border-[#FF9500]/30 bg-[#FF9500]/5">
              <AlertTriangle className="h-4 w-4 text-[#FF9500]" />
              <AlertDescription className="text-sm">
                <strong>Mandatory Reporter Notice:</strong><br />
                {categories?.legal_text?.mandatory_reporter}
              </AlertDescription>
            </Alert>

            <Alert className="border-red-500/30 bg-red-500/5">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-sm">
                <strong>False Reporting Warning:</strong><br />
                {categories?.legal_text?.false_reporting}
              </AlertDescription>
            </Alert>

            <Alert className="border-blue-500/30 bg-blue-500/5">
              <Shield className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-sm">
                <strong>Law Enforcement Disclosure:</strong><br />
                {categories?.legal_text?.law_enforcement}
              </AlertDescription>
            </Alert>

            {(formData.subject_age_approximate === 'minor_under_13') && (
              <Alert className="border-purple-500/30 bg-purple-500/5">
                <Users className="h-4 w-4 text-purple-500" />
                <AlertDescription className="text-sm">
                  <strong>Minor Under 13 Guidance:</strong><br />
                  {categories?.legal_text?.minor_guidance}
                </AlertDescription>
              </Alert>
            )}

            <Separator />

            {/* Acknowledgment Checkboxes */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-lg border border-border">
                <Checkbox
                  id="mandatory-reporter"
                  checked={formData.mandatory_reporter_acknowledged}
                  onCheckedChange={(checked) => updateField('mandatory_reporter_acknowledged', checked)}
                  className="mt-1"
                />
                <Label htmlFor="mandatory-reporter" className="cursor-pointer text-sm">
                  I acknowledge that I may be a mandatory reporter under state law and understand my reporting obligations.
                </Label>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg border-2 border-primary/50 bg-primary/5">
                <Checkbox
                  id="legal-ack"
                  checked={formData.legal_acknowledgment}
                  onCheckedChange={(checked) => updateField('legal_acknowledgment', checked)}
                  className="mt-1"
                />
                <Label htmlFor="legal-ack" className="cursor-pointer text-sm">
                  <strong>I acknowledge and agree to the following:</strong>
                  <ul className="list-disc ml-4 mt-2 space-y-1 text-muted-foreground">
                    <li>The information I am providing is true and accurate to the best of my knowledge</li>
                    <li>I understand this information may be shared with law enforcement if necessary</li>
                    <li>I consent to Players Health using this information to investigate and take appropriate action</li>
                  </ul>
                </Label>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={() => setCurrentStep(4)}>
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.legal_acknowledgment}
              className="bg-[#FF9500] hover:bg-[#FF9500]/90"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Report
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
